import {types, flow, getEnv, getType, resolveIdentifier, getRoot} from "mobx-state-tree";
import Group from "../../../../models/Group";
import {TagSelectState} from "../../../elements/tags/TagSelectState";
import {GroupSelectState} from "../../../elements/groups/GroupSelectState";
import Notification from "../../../../utils/Notification";
import {DataExportTypeSelectState} from "../../../elements/dataExportType/DataExportTypeSelectState";
import {DimensionSelectViewState} from "../../../elements/dimensions/dimensionSelectViewState";
import lodash from "lodash";
import { DataExportType} from "../../../../models/data/exports/DataExportType";
import { DataDeliveryType } from "../../../../models/data/exports/DataDeliveryType";
import {DimensionsMultiSelectState} from "../../../elements/dimensions/multi-select/dimensionsMultiSelectState";
import {createCustomAjvValidator} from "../../../../services/AjvValidator";
import {DataDeliveryTypeSelectState} from "../../../elements/dataDeliveryType/DataDeliveryTypeSelectState";
import {isJSONSchemaValid} from "../../../../utils/common";
import moment from 'moment';
import Ajv from "ajv";
import addFormats from "ajv-formats";
import {
  deliveryTypes,
  QUERY_AGGREGATE_RX,
  QUERY_TIMEPERIOD_RX
} from "../../../../utils/constants";
import {ModalState} from "../../../elements/modal/GenericModal";
import {FormatModalState} from "../modal/settings/FormatModal";
import {TimeZoneModalState} from "../modal/settings/TimeZoneModal";
import DataExportTypeStore from "../../../../stores/domain/DataExportTypeStore";
import {IncludeModalState} from "../modal/settings/IncludeModal";

const defaultSchedulerUISchema = {
  "additionalFilters": {
    "ui:options": {
      "label": false
    },
    "toggle": {
      "ui:widget": "timeFilterToggle",
      "ui:options": {
        "label": false,
      }
    },
    "definition": {
      "ui:options": {
        "label": false
      },
      "betweenFilters": {
        "ui:options": {
          "label": false,
        },
        "ui:widget": "singleBetweenFilter"
      }
    }
  },
  "interval": {
    "ui:options": {
      "label": false
    },
    "toggle": {
      "ui:widget": "scheduleToggle",
      "ui:options": {
        "label": false
      },
    },
    "definition": {
      "ui:options": {
        "label": false
      },
      "data": {
        "ui:options": {
          "label": false
        },
        "cronExpression": {
          "ui:widget": "cronSelect",
          "ui:options": {
            "label": false,
          },
        },
      }
    }
  }
};
const defaultUISchema = {
  "columns": {
    'ui:widget': 'dualListBox'
  },
  "filter": {
    'toggle': {
      'ui:options': {
        'label': false
      },
      'ui:widget': 'filterToggle',
    },
    'ui:options': {
      'label': false
    },
    'definition': {
      'ui:options': {
        'label': false
      },
      "onColumns":{
        "items": {
          "name": {
            'ui:widget': 'redshiftColumnSelect',
            "ui:placeholder": "Select"
          }
        }
      },
    }
  },
  "useOr": {
    'ui:options': {
      'label': false
    },
    'ui:widget': 'filterToggle',
  },
  "orderBy": {
    'toggle': {
      'ui:options': {
        'label': false
      },
      'ui:widget': 'orderByToggle',
    },
    'ui:options': {
      'label': false
    },
    'definition': {
      'ui:options': {
        'label': false
      },
      "name": {
        'ui:widget': 'redshiftColumnSelect',
        "ui:placeholder": "Select"
      }
    }
  }
};

const DataExportFormState = types.model('DataExportFormState', {
  name: types.optional(types.string, ""),
  format: types.optional(types.string, "CSV"),
  timezone: types.optional(types.string, "UTC"),
  isLoading: types.optional(types.boolean, false),
  tagSelectStore: types.maybe(types.late(() => types.reference(TagSelectState))),
  selectedTags: types.optional(types.array(types.string), []),
  groupSelectStore: types.maybe(types.late(() => types.reference(GroupSelectState))),
  selectedGroups: types.maybe(types.array(types.safeReference(Group))),
  dataExportTypeSelectStore: types.maybe(types.late(() => types.reference(DataExportTypeSelectState))),
  dataDeliveryTypeSelectStore: types.maybe(types.late(() => types.reference(DataDeliveryTypeSelectState))),
  dataExportTypeSelected: types.maybe(types.reference(DataExportType)),
  dataDeliveryTypeSelected: types.maybe(types.reference(DataDeliveryType)),
  dataDeliveryTypeUISchema: types.optional(types.string, '{}'),
  data: types.optional(types.string, '{}'),
  schedulerData: types.optional(types.string, '{}'),
  deliveryData: types.optional(types.string, '{}'),
  formHasErrors: types.optional(types.boolean, false),
  modalStore: types.maybe(ModalState),
  formatModalState: types.maybe(types.late(() => FormatModalState)),
  timeZoneModalState: types.maybe(types.late(() => TimeZoneModalState)),
  includeModalState: types.maybe(types.late(() => IncludeModalState)),
  errorMessage: types.optional(types.string, "Null"),
  // isExclusivelyGroupOwned: types.optional(types.boolean, true),
  dimensionSelectViewState: types.maybeNull(types.reference(DimensionSelectViewState)),
  dimensionsMultiSelectStore: types.maybeNull(types.late(() => types.reference(DimensionsMultiSelectState))),
  dimensionAll: types.optional(types.boolean, true),
  isDimensionSelectVisible: types.optional(types.boolean, false),
  liveValidateSchema: types.optional(types.boolean, false),
  hideActions: types.optional(types.array(types.string), []),
  deliveryEnabled: types.optional(types.boolean, false),
  isDeliveryOpened: types.optional(types.boolean, false),
  userEmail: types.optional(types.string, ""),
  defaultExcludedStatus: types.array(types.string),
  isCodeStatusExcludedByDefault: types.optional(types.boolean, true),
  v1ScanCountSetting: types.optional(types.boolean, false),
}).volatile((self) => ({
  columnWithTransformation: {},
  modalComponent: types.object,
  modalComponentStore: types.object,
  modalSize: types.str,
  modalClassName: types.str,
})).actions(self => ({
  handleConfigModalPopUp(component, title, componentStore, size = "", className = "") {
    self.modalComponentStore = self[componentStore];
    self.modalComponent = component;
    self.modalSize = size;
    self.modalClassName = className;
    self.modalStore.toggle()
    self.modalStore.handleTitle(title)
  },
  setDefaults() {
    self.isLoading = true;
    self?.setOnSelectChanges();
    self?.dataExportTypeSelectStore?.store?.getDataExportTypes()?.forEach(item => {
      if (item.type === 'default') {
        self?.dataExportTypeSelectStore?.select(item?.type);
        self?.selectDataExportType(item);
      };
    });
    // self?.populateDefaultDelivery();

    self?.groupSelectStore?.selectGroup([]);
    self?.tagSelectStore?.setSelectedTags([]);
    // self?.tagSelectStore?.tagStore?.setFilterOn(self?.groupSelectStore?.groupStore?.groups[0].uuid);
    // TODO: if no group is selected then what to filter it by?
    self?.dimensionSelectViewState?.reset();

    self.isLoading = false;
  },
  populateDefaultDelivery() {
    if(lodash.isEmpty(self?.dataDeliveryTypeSelected)) {
      self?.dataDeliveryTypeSelectStore?.store?.getDataDeliveryTypes()?.forEach(item => {
        if (item.type === deliveryTypes.EMAIL) {
          self?.dataDeliveryTypeSelectStore?.select(item?.type);
          self?.selectDataDeliveryType(item);
          self?.populateDefaultEmail(self?.userEmail);
        };
      });
    }
  },
  populateDefaultEmail(email) {
    const data = self.getDeliveryFormData();
    data.emails = [email || ""]; // Directly assign an array with the email
    self.updateDeliveryData(data);
  },
  setUserEmail(user) {
    self.userEmail = user?.email;
  },
  setDeliveryEnabled(flag) {
    self.deliveryEnabled =  flag
  },
  handleNameChange(e) {
    self.name = e?.target?.value;
  },
  handleHasErrors(boolean) {
    self.formHasErrors = boolean;
  },
  handleErrorMessage(message) {
    self.errorMessage = message;
  },
  openDelivery() {
    self.isDeliveryOpened = !self.isDeliveryOpened
    self.populateDefaultDelivery();
  },
  setOnSelectChanges() {
    self.groupSelectStore?.setMultiSelectConsumer(self.selectGroup);
    self.tagSelectStore?.setConsumer(self.selectTag);
    self.dataExportTypeSelectStore?.setConsumer(self.selectDataExportType);
    self.dataDeliveryTypeSelectStore?.setConsumer(self.selectDataDeliveryType);
  },
  selectGroup(arr) {
    arr = arr || [];
    self.selectedGroups = arr.flatMap((group) => group.uuid);
  },
  selectTag(arr) {
    self.selectedTags = arr.flatMap((tag) => tag.uuid);
  },
  selectDataExportType(obj) {
    self.dataExportTypeSelected = obj;

    try {
      // Set columns that need transformation
      self.columnWithTransformation = JSON.parse(self?.dataExportTypeSelected?.uiSchema)?.columns?.["ui:options"]?.columnOptions?.reduce((tMap, columnOption) => {
        (columnOption?.options || []).forEach(op => {
          if (op.hasOwnProperty("transformation")) {
            tMap[op.value] = op;
          }
        });
        return tMap;
      }, {}) ?? {};
    } catch (e) {
      self.columnWithTransformation = {};
    }

    // clear data when export type is updated
    self.data = JSON.stringify({});
    self.schedulerData = JSON.stringify({});
  },
  selectDataDeliveryType(obj) {
    self.dataDeliveryTypeSelected = obj
    self.dataDeliveryTypeUISchema = obj?.uiSchema
  },
  toggleIsExclusivelyGroupOwned(){
    self.isExclusivelyGroupOwned = !self.isExclusivelyGroupOwned
  },
  toggleDimensionAll(){
    self.dimensionAll = !self.dimensionAll
    self.toggleIsDimensionSelectVisible(!self.dimensionAll)
    self.dimensionAll ? self.dimensionsMultiSelectStore.selectAllDimensions() : self.dimensionsMultiSelectStore.reset()
  },
  toggleIsDimensionSelectVisible(visible){
    self.isDimensionSelectVisible = visible
  },
  addTransformationForColumns(data) {
    if (!data.transformations) {
      data.transformations = { toggle: false };
    }

    const selectedCols = data.columns?.filter(Boolean) ?? [];
    const mergedDefinition = {};
    let needsTransformation = false;

    if (selectedCols.length && selectedCols.some(col => col && (col in self.columnWithTransformation))) {
      needsTransformation = true;

      selectedCols.forEach(col => {
        if (col in self.columnWithTransformation && self.columnWithTransformation[col]?.transformation) {
          lodash.mergeWith(
            mergedDefinition,
            self.columnWithTransformation[col]?.transformation,
            (objVal, srcVal) => {
              if (Array.isArray(objVal) && Array.isArray(srcVal)) {
                return [...objVal, ...srcVal];
              }
            }
          );
        }
      });

      // Pivot
      // We can't have function columns without period columns. So we remove pivot
      if (
        !lodash.isEmpty(mergedDefinition) &&
        lodash.isEmpty(mergedDefinition?.pivot?.headers) &&
        !lodash.isEmpty(mergedDefinition?.pivot?.values)
      ) {
        delete mergedDefinition.pivot;
      }

      // If mergedDefinition is still empty, toggle back to 'false'
      if (lodash.isEmpty(mergedDefinition)) needsTransformation = false;
    }

    data.transformations.toggle = needsTransformation;
    data.transformations.definition = needsTransformation && !lodash.isEmpty(mergedDefinition) ? mergedDefinition : {};

    return data;
  },
  updateExportData(data) {
    if (self.includeModalState?.excludedFlag) {
      // If the user has applied a 'Status' filter, override and disable the default filter settings
      const statusColumn = self.getStatusColumn();
      const filterOnStatusFound = data?.filter?.definition?.onColumns?.filter(f => f.name === statusColumn.value)?.length > 0;
      if (filterOnStatusFound) {
        self.includeModalState?.updateExcludedFlag(false);
      }
    }
    // Add Default Transformation for selected columns
    data = self.addTransformationForColumns(data);
    self.data = JSON.stringify(data);
  },
  updateExportScheduleData(data) {
    self.schedulerData = JSON.stringify(data);
  },
  updateDeliveryData(data) {
    self.deliveryData = JSON.stringify(data);
  },
  updateExportFormat(format) {
    self.format = format;
  },
  updateTimeZone(timezone) {
    self.timezone = timezone;
  },
  updateIsCodeStatusExcludedByDefault(val) {
    self.isCodeStatusExcludedByDefault = val;
  },
  updateV1ScanCountSetting(val) {
    self.v1ScanCountSetting = val;
  },
  convertTimeInExportDefinition(exportItem, fromTimezone, toTimezone) {
    try {
      const convertTime = (datetime, fromTimezone, toTimezone) => moment.tz(datetime, fromTimezone).tz(toTimezone).format("YYYY-MM-DDTHH:mm:ss");
      const isValidDate = (dateStr) => moment(dateStr).isValid();

      // Convert Absolute Time Value
      // onColumn Filters
      for (const filter of exportItem?.definition?.filter?.definition?.onColumns) {
        if (filter.value && isValidDate(filter.value)) {
          filter.value = convertTime(filter.value, fromTimezone, toTimezone);
        }
      }

      // betweenFilters
      for (const timeFilters of [
        ...(exportItem?.definition?.filter?.definition?.betweenFilters || []),
        ...(exportItem?.schedule?.additionalFilters?.definition?.betweenFilters || [])
      ]) {
        if (timeFilters.startValue && !timeFilters.startValue.startsWith("{{")) {
          timeFilters.startValue = convertTime(timeFilters.startValue, fromTimezone, toTimezone);
        }
        if (timeFilters.endValue && !timeFilters.endValue.startsWith("{{")) {
          timeFilters.endValue = convertTime(timeFilters.endValue, fromTimezone, toTimezone);
        }
      }

      // TODO: Only supports a single date filter for now,
      //  extend this logic when multiple date filters are introduced
    } catch (e) {
      console.error("convertTimeInExportDefinition Error: Failed to add convert time");
      console.error(e);
    }
    return exportItem;
  },
  transformErrors(errors) {
    return errors.map(error => {
      const errorMessages = {
        minItems: {
          ".columns": "Please choose at least one column to export."
        },
        oneOf: {
          ".filter": "",
          ".orderBy": "",
          ".additionalFilters": "",
          ".interval": "",
          ".interval.definition": "",
          ".interval.definition.data": ""
        },
        const: {
          ".filter.toggle": "",
          ".orderBy.toggle": "",
          ".additionalFilters.toggle": "",
          ".interval.toggle": "",
        },
        required: {
          ".filter.definition.onColumns.0.name": "Please select a column to add a filter.",
          ".filter.definition.onColumns.0.value": "Please enter a value.",
          ".orderBy.definition.0.name": "Please select a column to add a sort order.",
          ".interval.definition.data.expression": "Please enter a valid cron expression.",
          ".interval.definition.data.on": "Please select a date to run the export.",
          ".interval.definition.data.rate.duration": "Please enter a valid integer for the duration."
        }
      };

      const caseHandler = errorMessages[error.name]?.[error.property];
      if (caseHandler !== undefined) {
        error.message = caseHandler;
      }
      return error;
    });
  },
  isFormValid() {
    const isExportDetailsValid = isJSONSchemaValid(self?.getSelectedExportSchema(), self?.getFormData());
    const isExportScheduleValid = isJSONSchemaValid(self?.getSelectedExportScheduleSchema(), self?.getExportScheduleFormData());

    let invalidScheduleData = false;
    const schedulerData = self?.getExportScheduleFormData();
    if (schedulerData?.additionalFilters?.toggle) {
      const singleFilter = schedulerData?.additionalFilters?.definition?.betweenFilters[0];

      if ((lodash.isEmpty(singleFilter?.startValue) && lodash.isEmpty(singleFilter?.endValue))) {
        invalidScheduleData = true;
        console.error('DataExportFormState.isFormValid: startValue and endValue both cannot be empty');
      }

      const isRelative = singleFilter?.startValue?.includes("{{");
      if (
        !isRelative &&
        !lodash.isEmpty(singleFilter?.startValue) &&
        !lodash.isEmpty(singleFilter?.endValue) &&
        !moment(singleFilter?.startValue).isBefore(moment(singleFilter?.endValue))) {
        invalidScheduleData = true;
        console.error('DataExportFormState.isFormValid: startValue should be before endValue');
      }

      if (isRelative) {
        const [duration, unit] = singleFilter?.startValue?.substring(
          singleFilter?.startValue?.indexOf("{{") + 2,
          singleFilter?.startValue?.lastIndexOf("}}")
        )?.split(" ");
        if (isNaN(parseInt(duration))) {
          invalidScheduleData = true;
          console.error('DataExportFormState.isFormValid: Invalid duration');
        }
      }
    }
    if (schedulerData?.interval?.toggle) {
      const schedule = schedulerData?.interval?.definition;

      if (lodash.isEmpty(schedule?.data?.cronExpression)) {
        invalidScheduleData = true;
      }
    }
    const valid = isExportDetailsValid && isExportScheduleValid && !invalidScheduleData;
    self.liveValidateSchema = !valid;

    return valid;
  },
  validateDeliveryDetailsForm() {
    const schema = JSON.parse(self?.dataDeliveryTypeSelected?.schema);
    const data = self.getDeliveryFormData();
    const ajv = new Ajv({allErrors: true, validateFormats: false});
    addFormats(ajv);
    const validate = ajv.compile(schema);
    let valid = validate(data);
    if (!valid) {
      self.liveValidateSchema = true;
      console.error(`${getType(self).name} Error: Failed to validate form data`, validate.errors);
    }
    return valid;
  },
  update: flow(function* (uuid) {
    self.isLoading = true;
    const isValidDimensionAndForm = self.validateDimension() && self?.isFormValid();
    const isValidDeliveryDetails = self.isDeliveryOpened ? self.validateDeliveryDetailsForm() : true;
    if (isValidDimensionAndForm && isValidDeliveryDetails) {
      self.handleHasErrors(false);
      const response = yield getEnv(self).dataExportManager.update(uuid, self);
      if (self.formHasErrors) {
        new Notification()
          .setType("error")
          .setMessage(`${self.name} Data Export failed updating`)
          .send();
      } else {
        new Notification()
          .setType("success")
          .setMessage(`${self.name} Data Export updated`)
          .send();
        self.handleHasErrors(false);
      }
      self.isLoading = false;
      return response;
    }
    self.isLoading = false;
    return null;
  }),
  getSelectedDimensions() {
    return self.dimensionsMultiSelectStore.getTranspiledDimensions();
  },
  validateDimension() {
    if (!self.dimensionSelectViewState?.dimensionsEnabled) return true;
    const selectedDimensions = self.getSelectedDimensions();
    if (selectedDimensions && Object.values(selectedDimensions).some(l => l.length)) {
      // TODO: validate
    }
    return true;
    // if ((!lodash.isEmpty(selectedDimensions) &&
    //     !Object.values(selectedDimensions).every(l => !l.length)) || self.dimensionAll) {
    //   return true;
    // }
    // new Notification()
    //   .setType("error")
    //   .setMessage("Please select at least one dimension")
    //   .send();
    // return false;
  },
  saveNew: flow(function* () {
    self.isLoading = true;
    const isValidDimensionAndForm = self.validateDimension() && self?.isFormValid();
    const isValidDeliveryDetails = self.isDeliveryOpened ? self.validateDeliveryDetailsForm() : true;
    if (isValidDimensionAndForm && isValidDeliveryDetails) {
      self.handleHasErrors(false);
      const response = yield getEnv(self).dataExportManager.save(self);
      if (!lodash.isEmpty(response?.data)) {
        self.name = response.data.name;
      }
      if (self.formHasErrors) {
        new Notification()
          .setType("error")
          .setTitle("Data Export Creation Failed")
          .setMessage(self.errorMessage?.split(':')[1]?.trim() || `Please check export definition`)
          .send();
      } else {
        new Notification()
          .setType("success")
          .setTitle("Data Export Created")
          .send();
        self.handleHasErrors(false);
      }
      self.isLoading = false;
      return response;
    }
    self.isLoading = false;
    return null;
  }),
  fetchItemByName: flow(function* (name, queryParams={}) {
    return yield getEnv(self).dataExportManager.fetchAllForMe({...queryParams, name}, self);
  }),
  fetchItemByUUID: flow(function* (id, queryParams={}) {
    return yield getEnv(self).dataExportManager.fetchByUUID(id, queryParams, self);
  }),
  fetchModelAndHydrate: flow(function* (id, queryParams={}){
    if (!id) {
      console.warn(`${getType(self).name} Warn: ID is 'undefined' or 'null'`);
    }
    let response;
    self.isLoading = true;
    self?.setOnSelectChanges();
    try {
      // TODO: remove
      response = yield getEnv(self).dataExportManager.fetchByUUID(id, queryParams, self);
      if (response) {
        self.hydrateForm(response);
        self.handleHasErrors(false);
      } else {
        self.handleHasErrors(true);
      }
    } catch (e) {
      console.error(`${getType(self).name} Error: Failed to Fetch and Hydrate data for '${id}'`, e);
    }
    self.isLoading = false;
    return response;
  }),
  hydrateDimension(dimensions = {}){
    const isDimensionAll = Object.values(dimensions).every(item => lodash.isEmpty(item));
    self.dimensionAll = !isDimensionAll
    self.toggleDimensionAll()
    if (!self.dimensionAll) {
      self.dimensionsMultiSelectStore.hydrateDimensions(dimensions)
    }
  },
  hydrateForm(item){
    self.isLoading = true;
    try {
      self.name = item?.name;
      self.format = item?.format;
      self.timezone = item?.timezone;
      self.dataExportTypeSelected = item?.type;
      self.deliveryData = JSON.stringify(item?.deliveryInfo?.deliveryData);
      self.dataDeliveryTypeSelected = item?.deliveryInfo?.type;
      self.isDeliveryOpened = item?.deliveryInfo?.hasDeliveryInfo;
      self.dataExportTypeSelectStore?.select(item?.type);
      self.dataDeliveryTypeSelectStore?.select(item?.deliveryInfo?.type);
      self.groupSelectStore?.selectGroup(item?.owner?.groups);
      let tagUuids;
      if (item?.tags && typeof item?.tags[0] === 'string') {
        tagUuids = item?.tags;
      } else {
        tagUuids = item?.tags?.flatMap(t => t.uuid);
      }
      self.tagSelectStore?.setSelectedTags(tagUuids);
      self.hydrateDimension(item?.owner?.dimensions || {});
      self.hideActions = item?.hideActions;
      if (self.includeModalState?.excludedFlag) {
        const defaultStatusFilterFound = (item?.definition?.filter?.definition?.onColumns?.filter(f => self.defaultExcludedStatus.includes(f.value) && f.condition === "!="))?.length === self.defaultExcludedStatus?.length;

        if (defaultStatusFilterFound) {
          // Remove filters from definition
          for (const s of self.defaultExcludedStatus) {
            const filterIndex = item?.definition?.filter?.definition?.onColumns?.findIndex(f => f.value === s && f.condition === "!=");
            if (filterIndex > -1) {
              item.definition.filter.definition.onColumns.splice(filterIndex, 1);
            }
          }

          if (item.definition.filter.definition.onColumns.length === 0) {
            item.definition.filter.toggle = false;
          }
        }
        self.includeModalState?.updateExcludedFlag(defaultStatusFilterFound);
      }
      if (!self?.isTimeZoneDefault()) {
        self?.convertTimeInExportDefinition(item, "UTC", self?.timezone);
      }
      self.updateExportData(item?.definition);
      self.updateExportScheduleData(item?.schedule);
      self.updateV1ScanCountSetting(self.hasV1ScanCountQuery());
    } catch (err) {
      console.error(`${getType(self).name} Error: Failed to populate fields to view`, err);
    }
    self.isLoading = false;
  },
})).views((self) => ({
  getStatusColumn() {
    // Fetch 'Status' column name from filter options
    const dataExportTypeStore = resolveIdentifier(DataExportTypeStore, getRoot(self), 'dets');
    const exportSchema = dataExportTypeStore.dataExportTypes.find(e => e.type === "default");

    const exportUISchema = JSON.parse(exportSchema.uiSchema);

    return exportUISchema?.filter?.definition?.onColumns?.items?.name?.["ui:options"]?.columnOptions?.find(op => op.label === "QR Codes")?.options?.find(c => c.label === "Status");
  },
  settingsButtonColor(bool) {
    return bool ? "secondary" : "primary"
  },
  settingsClassName(bool) {
    return bool ? "btn-outline-secondary mr-1 mb-1" : "btn-outline-primary mr-1 mb-1"
  },
  isFormatDefault() {
    return self.format === "CSV";
  },
  getDefaultTimeZone(){
    return { value: "UTC", label: "UTC" };
  },
  hasV1ScanCountQuery() {
    return self.getFormData()?.setOperation?.type === "UNION";
  },
  isTimeZoneDefault() {
    return self.timezone === (self.getDefaultTimeZone()).value;
  },
  getTimeZoneOptions() {
    let options = [];
    options = Intl.supportedValuesOf('timeZone').map(timeZone => ({
      value: timeZone,
      label: timeZone
    }));

    // Update label for local timezone
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localTimeZoneIndex = options.findIndex(t => t.value === localTimeZone);
    if (localTimeZoneIndex > -1) {
      options[localTimeZoneIndex] = { value: localTimeZone, label: `${localTimeZone} (Local)` };
    }

    // Default: UTC
    options.unshift(self.getDefaultTimeZone());

    return options;
  },
  getAllExportSettings() {
    return ['timezone', 'format', 'inlcudeSetting'];
  },
  getSelectedExportSchema() {
    let schema = self?.dataExportTypeSelected?.definition || JSON.stringify({});
    return JSON.parse(schema);
  },
  getSelectedExportScheduleSchema() {
    let schema = self?.dataExportTypeSelected?.scheduler || JSON.stringify({});
    return JSON.parse(schema)?.definition || {};
  },
  getSelectedExportSchemaKey() {
    return self?.dataExportTypeSelected?.type || Date.now();
  },
  getSelectedExportUISchema() {
    let uiSchema = self?.dataExportTypeSelected?.uiSchema || JSON.stringify(defaultUISchema);
    return JSON.parse(uiSchema);
  },
  getSelectedExportScheduleUISchema() {
    let schema = self?.dataExportTypeSelected?.scheduler || JSON.stringify({});
    return JSON.parse(schema)?.uiSchema || defaultSchedulerUISchema;
  },
  getFormData() {
    return JSON.parse(self.data);
  },
  getDeliveryFormData() {
    let deliveryData = self?.deliveryData || JSON.stringify({})
    return JSON.parse(deliveryData);
  },
  getSelectedDeliverySchema() {
    let schema = self?.dataDeliveryTypeSelected?.schema || JSON.stringify({});
    return JSON.parse(schema)
  },
  getSelectedDeliveryUiSchema() {
    let uiSchema = self?.dataDeliveryTypeSelected?.uiSchema || JSON.stringify({});
    return JSON.parse(uiSchema)
  },
  getExportScheduleFormData() {
    return JSON.parse(self.schedulerData);
  },
  getExtraExportScheduleErrors() {
    let errors = {};
    const data = self?.getExportScheduleFormData();

    if (data?.additionalFilters?.toggle) {
      const singleFilter = data?.additionalFilters?.definition?.betweenFilters[0];
      const addErrorToBetweenFilters = (errMessage="") => {
        const errList = (errors?.additionalFilters?.definition?.betweenFilters?.__errors || []);
        errList.push(errMessage);
        errors = {
          ...errors,
          additionalFilters: {
            ...errors?.additionalFilters,
            definition: {
              ...errors?.additionalFilters?.definition,
              betweenFilters: {
                ...errors?.additionalFilters?.definition?.betweenFilters,
                __errors: errList
              }
            }
          }
        };
      }
      const isRelative = singleFilter?.startValue?.includes("{{");

      if (!isRelative) {
        // Validate Absolute Time

        if (
          !lodash.isEmpty(singleFilter?.startValue) &&
          !lodash.isEmpty(singleFilter?.endValue) &&
          !moment(singleFilter?.startValue).isBefore(moment(singleFilter?.endValue))
        ) {
          addErrorToBetweenFilters("Start date should be before End date.");
        }

        if (lodash.isEmpty(singleFilter?.startValue) && lodash.isEmpty(singleFilter?.endValue)) {
          addErrorToBetweenFilters("Please provide a valid time filter.");
        }
      } else {
        const [duration, unit] = singleFilter?.startValue?.substring(
          singleFilter?.startValue?.indexOf("{{") + 2,
          singleFilter?.startValue?.lastIndexOf("}}")
        )?.split(" ");
        if (isNaN(parseInt(duration))) {
          addErrorToBetweenFilters("Duration should be greater that 0.");
        }
      }
    }
    if (data?.interval?.toggle) {
      const schedule = data?.interval?.definition;
      const addErrorToSchedule = (errMessage="") => {
        const errList = (errors?.interval?.definition?.data?.cronExpression?.__errors || []);
        errList.push(errMessage);
        errors = {
          ...errors,
          interval: {
            ...errors?.interval,
            definition: {
              ...errors?.interval?.definition,
              data: {
                ...errors?.interval?.definition?.data,
                cronExpression: {
                  ...errors?.interval?.definition?.data?.cronExpression,
                  __errors: errList
                }
              }
            }
          }
        };
      }
      if (lodash.isEmpty(schedule?.data?.cronExpression)) {
        addErrorToSchedule("Please select a valid schedule option.");
      }
    }
    return self.liveValidateSchema ? errors : {};
  },
  getExtraExportDetailErrors(intl) {
    let errors = {};
    const data = self?.getFormData();

    const addErrorToColumn = (msg="") => {
      errors = {
        ...errors,
        columns: {
          __errors: [...(errors?.columns?.__errors || []), msg]
        }
      };
    };

    if (self.liveValidateSchema && lodash.isEmpty(data?.columns?.filter(n => n))) {
      addErrorToColumn(intl.formatMessage({id: 'data.exports.validation.noColumnSelected.defaultMessage'}));
    }

    if (!lodash.isEmpty(data?.columns?.filter(n => n))) {
      const selectedColumns = data?.columns?.filter(n => n);
      const asserOnlyOneSelectedColumn = (tableName, regPattern) => {
        if (selectedColumns.filter(column => column.match(regPattern))?.length > 1) {
          addErrorToColumn(`${intl.formatMessage({id: 'data.exports.validation.asserOnlyOneSelectedColumn.defaultMessage'})} '${tableName}'.`);
        }
      }

      // Show error when user tries to select more than one column
      asserOnlyOneSelectedColumn("Time Period", QUERY_TIMEPERIOD_RX);
      asserOnlyOneSelectedColumn("Tag Name", new RegExp('.*Tag Name.*', 'i'));
      asserOnlyOneSelectedColumn("Dimension Name", new RegExp('.*Dimension Name.*', 'i'));

      // Show error when user selects a period column without function column
      const hasPeriodColumn = selectedColumns.some(col => col.match(QUERY_TIMEPERIOD_RX));
      const hasFunctionColumn = selectedColumns.some(col => col.match(QUERY_AGGREGATE_RX) && !col.match(QUERY_TIMEPERIOD_RX));

      if (hasPeriodColumn && !hasFunctionColumn) {
        addErrorToColumn(intl.formatMessage({id: 'data.exports.validation.periodColumnWithoutFunction.defaultMessage'}));
      }
    }
    return errors;
  },
}))

export default DataExportFormState;
